<template>
  <div class="filters-container">
    <div class="filters">
      <div class="filter-buttons" :class="{'filter-buttons__clearable': canClearFilters}" v-if="!isNewFilterButtonHidden || canClearFilters">
        <b-dropdown data-cy="add-filter-dropdown" v-model="filterModel" @change="filterSelected" v-if="!isNewFilterButtonHidden && availableFilters.length > 0">
          <template #trigger>
            <b-button data-cy="add-filter-btn" icon-left="mdi mdi-filter-plus-outline" label="Add Filter" type="is-primary" />
          </template>
          <b-dropdown-item v-for="(item, index) in coreFilters" :value="item" :key="'core-'+index">{{ item.displayName }}</b-dropdown-item>
          <template v-if="displayedCustomFilters.length > 0 || totalHiddenCount > 0">
            <b-dropdown-item separator v-if="coreFilters.length > 0" />
            <b-dropdown-item v-for="(item, index) in displayedCustomFilters" :value="item" :key="'custom-'+index">{{ item.displayName }}</b-dropdown-item>
            <b-dropdown-item v-if="totalHiddenCount > 0" custom class="show-all-filters-item">
              <a @click.stop="toggleShowAllFilters" class="show-all-filters">
                <span class="icon is-small"><i class="mdi" :class="showAllFilters ? 'mdi-filter-minus-outline' : 'mdi-filter-plus'"></i></span>
                <span>{{ showAllFilters ? 'Show Less' : `Show All (${totalHiddenCount} more)` }}</span>
              </a>
            </b-dropdown-item>
          </template>
        </b-dropdown>
        <b-button data-cy="clear-filters-btn" v-if="canClearFilters" label="Clear Filters" type="is-text" @click="selectedFilters = []; $emit('filters-cleared')" />
      </div>
      <button
        v-if="selectedFilters.length > 0"
        class="button is-link is-outlined mr-2" style="line-height: 1.2;"
        data-cy="filters-toggle-btn"
        @click="toggleFiltersVisibility"
        :title="filtersExpanded ? 'Hide Filters' : 'Show Filters'">
        <i class="mdi" :class="filtersExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right'"></i>
        <span>{{ filtersExpanded ? 'Hide' : 'Show' }} Filters ({{ selectedFilters.length }})</span>
      </button>
      <div v-if="filtersExpanded" class="selected-filters-wrapper" data-cy="selected-filters-wrapper">
        <b-dropdown data-cy="active-filter-dropdown" class="filter-dropdown" v-for="(filter, index) in selectedFilters" :key="filter.id" :ref="`filter${index}`" @active-change="filterOpened(index, $event)">
          <template #trigger>
            <div class="filter">
              <p>
                {{ filter.displayName }}
                <template v-if="selectedFilters[index].value !== undefined || (filter.negative && filter.allowNegative !== false)">
                  <span>{{ getOperatorDisplayText(filter) }}</span>
                </template>
                {{ getValueForDisplay(filter) }}
              </p>
              <button v-if="filterSettings.deletableFilters !== false" @click.stop="removeFilter(index, filter.id)">
                <i class="mdi mdi-close-circle"></i>
              </button>
            </div>
          </template>
          <b-dropdown-item custom>
            <validation-observer v-slot="{ handleSubmit, meta: { valid } }" class="validation-observer">
              <div class="filter-content" v-on:keypress.enter="handleSubmit(() => { applyFilter(index, filter.id) })">
                <div class="filter-controls">
                  <div class="negative-toggle" v-if="filter.allowNegative !== false">
                    <b-checkbox v-model="tempNegative[filter.id]">{{ filter.availableOperators && filter.availableOperators.length > 1 ? "Doesn't" : 'Not' }}</b-checkbox>
                  </div>
                  <div class="operator-select" v-if="filter.availableOperators && filter.availableOperators.length > 1">
                    <b-select v-model="tempOperator[filter.id]" placeholder="Operator" expanded>
                      <option v-for="op in filter.availableOperators" :key="op" :value="op">
                        {{ operatorLabels[op] }}
                      </option>
                    </b-select>
                  </div>
                </div>
                  <div class="filter-control-input">
                    <b-select v-if="filter.type == FilterType.ENUM && !filter.isInline" expanded :placeholder="`Select ${filter.displayName}`" v-model="tempValue[filter.id]">
                      <option v-for="(option, index) in enumValuesForFilter(filter)" :key="index" :value="option.key">{{ option.value }}</option>
                    </b-select>
                    <div v-else-if="filter.type == FilterType.DATETIMERANGE">
                      <vue-date-picker range :partial-range="false" :month-change-on-scroll="false" v-model="tempValue[filter.id]"></vue-date-picker>
                    </div>
                    <div v-else-if="tempOperator[filter.id] === 'between'" class="between-inputs">
                      <input-with-validation b-name="input" field-label=" " class="number-input" :rules="filter.integerOnly ? 'required|integer' : 'required|decimal'" placeholder="From" v-model="tempBetweenFrom[filter.id]"></input-with-validation>
                      <span class="between-separator">and</span>
                      <input-with-validation b-name="input" field-label=" " class="number-input" :rules="filter.integerOnly ? 'required|integer' : 'required|decimal'" placeholder="To" v-model="tempBetweenTo[filter.id]"></input-with-validation>
                    </div>
                    <div v-else class="filter-input">
                      <component :is="getFilterComponentName(filter)" :filter="filter" v-bind="getFilterAttributes(filter)" v-model="tempValue[filter.id]"></component>
                    </div>
                  </div>
                  <button data-cy="apply-filter-btn" class="button is-primary" :disabled="!valid" @click="handleSubmit(() => { applyFilter(index, filter.id) })">Apply</button>
              </div>
            </validation-observer>
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>
  </div>
</template>


<script>
import {FilterType, TableFilter, OperatorDisplayTexts} from "@/models/TableFilter";
import TableFilterRadioGroup from "@/components/TableFilterRadioGroup.vue";
import {VueDatePicker} from "@vuepic/vue-datepicker"
import '@vuepic/vue-datepicker/dist/main.css';
import AutocompleteTableFilter from "@/components/table_filters/AutocompleteTableFilter";
import InputWithValidation from "@/components/validation/InputWithValidation.vue";

export default {
  name: "TableFilters",
  components: { TableFilterRadioGroup, VueDatePicker, AutocompleteTableFilter, InputWithValidation },
  props: {
    urlFilters: {
      type: Object,
      default: () => { return {} }
    },
    availableFilters: {
      type: Array,
      required: true,
      validator(value) {
        return Array.isArray(value) && value.every(x => x instanceof TableFilter)
      }
    },
    filterSettings: {
      type: Object,
      default: () => { return {} }
    }
  },
  computed: {
    isNewFilterButtonHidden() {
      return this.filterSettings.hiddenNewFilterButton
    },
    canClearFilters() {
      return this.selectedFilters.length > 0 && !this.filterSettings.hiddenClearFiltersButton
    },
    isSuperuser() {
      return this.$store.state.currentUser?.role?.kind === 'superuser'
    },
    coreFilters() {
      return this.availableFilters.filter(f => !f.name.startsWith('custom_fields['))
    },
    customFilters() {
      return [...this.availableFilters.filter(f => f.name.startsWith('custom_fields['))].sort((a, b) => a.displayName.localeCompare(b.displayName))
    },
    displayedCustomFilters() {
      if (this.isSuperuser || this.showAllFilters) {
        return this.customFilters
      }
      return this.customFilters.filter(f => !f.hidden)
    },
    totalHiddenCount() {
      if (this.isSuperuser) return 0
      return this.customFilters.filter(f => f.hidden).length
    }
  },
  watch: {
    urlFilters: {
      handler() {
        this.parseUrlParams()
      },
      immediate: true
    },
    availableFilters: {
      handler() {
        this.reloadDynamicFilters()
        this.parseUrlParams()
      },
      deep: true
    }
  },
  methods: {
    enumValuesForFilter(filter) {
      return this.dynamicEnumValues[filter.name] || filter.enumValues || []
    },
    async _reloadDynamicFilters() {
      const requests = []
      this.availableFilters.forEach(filter => {
        if (filter.type == FilterType.ENUM && filter?.data?.apiEndpoint) {
          requests.push(this.$http.get(filter.data.apiEndpoint).then(response => {
            this.dynamicEnumValues[filter.name] = response.data.map(x => { return { key: x[filter.data.key] === null ? '_null_' : x[filter.data.key], value: x[filter.data.value] } })
            return { response, filter }
          }))
        }
      })
      const promiseData = await Promise.allSettled(requests)
      return promiseData.filter(x => x.status == 'fulfilled').map(x => x.value)
    },
    getValueForDisplay(filter) {
      let val = filter.value
      if (filter.type == FilterType.ENUM) {
        const selectedEnum = this.enumValuesForFilter(filter)?.filter(x => x.key == filter.value)[0]
        if (selectedEnum) {
          val = selectedEnum.value
        }
      } else if (filter.type == FilterType.DATERANGE) {
        if (Array.isArray(filter.value)) {
          const dates = filter.value.map(x => this.$helpers.formatDate(x, 'LL', true))
          val = `${dates[0]} and ${dates[1]}`
        }
      } else if (filter.type == FilterType.DATETIMERANGE) {
        if (Array.isArray(filter.value)) {
          const dates = filter.value.map(x => this.$helpers.formatDate(x, 'LLL'))
          val = `${dates[0]} and ${dates[1]}`
        }
      }  else if (filter.type == FilterType.DATE) {
        val = this.$helpers.formatDate(filter.value, 'LL', true)
      } else if (filter.operator === 'between' && Array.isArray(filter.value)) {
        val = `${filter.value[0]} and ${filter.value[1]}`
      } else {
        val = filter.value
      }
      return val
    },
    parseUrlParams() {
      this.selectedFilters = []
      for (const [key, raw] of Object.entries(this.urlFilters)) {
        if (!raw) continue
        if (Array.isArray(raw)) {
          raw.forEach(str => {
            let clause = null

            if (typeof str === 'string') {
              try { clause = JSON.parse(str) } catch { clause = null }
            } else {
              clause = str
            }
            if (!clause || typeof clause !== 'object') return

            if (key === 'custom_fields') {
              const filter = this.availableFilters.find(x => x.name == `custom_fields[${clause.field}]`)
              if (!filter) return
              this.filterSelected(filter, clause.v, false, !!clause.not, clause.op)
            } else {
              const filter = this.availableFilters.find(x => x.name == key)
              if (!filter) return
              this.filterSelected(filter, clause.v, false, !!clause.not, clause.op)
            }
          })
        }
      }
    },
    filterOpened(index, state) {
      if (state) {
        this.focusValueInput(index)
      }
    },
    filterSelected(filter, value = '', openFilter = true, negative = false, operator = null) {
      const randId = Math.random() + ''
      let val = value, dateStringVal = null
      if (filter.type == FilterType.ENUM && val === null) {
        val = '_null_'
      }
      if (filter.type == FilterType.DATERANGE && val) {
        val = val.map(x => this.$helpers.getDate({ date: x, withoutTimezone: true}).toDate())
        dateStringVal = val.map(x => this.$helpers.formatDate(x, 'YYYY-MM-DD', true))
      }
      if (filter.type == FilterType.DATETIMERANGE && val) {
        val = val.map(x => this.$helpers.getDate({ date: x, withoutTimezone: true}).toDate())
        dateStringVal = val
      }
      if (filter.type == FilterType.DATE && val) {
        val = this.$helpers.getDate({ date: val, withoutTimezone: true}).toDate()
        dateStringVal = this.$helpers.formatDate(val, 'YYYY-MM-DD', true)
      }
      const defaultOperator = operator || (filter.availableOperators ? filter.availableOperators[0] : 'equals')
      this.selectedFilters.push({
        id: randId,
        ...filter,
        value: dateStringVal || val,
        negative,
        operator: defaultOperator
      });
      if (val !== undefined && val !== '') {
        this.tempValue[randId] = val
      }
      this.tempNegative[randId] = negative
      this.tempOperator[randId] = defaultOperator
      
      // Handle between operator initialization
      if (Array.isArray(val) && val.length === 2) {
        this.tempBetweenFrom[randId] = val[0]
        this.tempBetweenTo[randId] = val[1]
      }
      this.assignColor(filter.name)
      if (openFilter) {
        this.filtersExpanded = true
        this.$nextTick(() => {
          this.filterModel = null
          const comp = this.$refs[`filter${this.selectedFilters.length - 1}`][0]
          comp.toggle()
          this.focusValueInput(this.selectedFilters.length - 1)
        })
      }
    },
    assignColor(name) {
      if (!this.assignedColors[name]) {
        const randomIndex = Math.floor(Math.random() * this.colors.length)
        let color = this.colors.splice(randomIndex, 1)[0]
        this.assignedColors[name] = color
      }
    },
    focusValueInput(index) {
      const comp = this.$refs[`filter${index}`][0]
      if (comp) {
        setTimeout(() => {
          const el = comp.$el.querySelector('input.input')
          if (el) {
            el.focus()
          }
        }, 100)
      }
    },
    removeFilter(index, id) {
      this.selectedFilters.splice(index, 1)
      delete this.tempValue[id]
      delete this.tempNegative[id]
      delete this.tempOperator[id]
      delete this.tempBetweenFrom[id]
      delete this.tempBetweenTo[id]
      this.emitActiveFilters()
    },
    applyFilter(index, id) {
      this.$refs[`filter${index}`][0].toggle()
      let val = null
      if (this.selectedFilters[index].type == FilterType.DATERANGE) {
        if (this.tempValue[id] && Array.isArray(this.tempValue[id])) {
          val = this.tempValue[id].map(x => this.$helpers.formatDate(x, 'YYYY-MM-DD', true))
        }
      } else if (this.selectedFilters[index].type == FilterType.DATETIMERANGE) {
        if (this.tempValue[id] && Array.isArray(this.tempValue[id])) {
          val = this.tempValue[id].map(x => {
            return this.$helpers.getDate({ date: x, withoutTimezone: true }).tz(this.$store.state.currentUser.timezone).utc().format('YYYY-MM-DD')
          })
        }
      } else if (this.selectedFilters[index].type == FilterType.DATE) {
        val = this.$helpers.formatDate(this.tempValue[id], 'YYYY-MM-DD', true)
      } else if (this.tempOperator[id] === 'between') {
        val = [this.tempBetweenFrom[id], this.tempBetweenTo[id]]
      } else {
        val = this.tempValue[id]
      }

      // removing empty filters, and arrays with empty values
      const isEmpty = val === null || val === undefined || val === '' ||
        (Array.isArray(val) && (val.length === 0 || val.every(x => x === null || x === undefined || x === '')))

      if (isEmpty) {
        this.removeFilter(index, id)
        return
      }

      this.selectedFilters[index].value = val
      this.selectedFilters[index].negative = this.tempNegative[id]
      this.selectedFilters[index].operator = this.tempOperator[id] || 'equals'
      this.emitActiveFilters()
    },
    emitActiveFilters() {
      const clauses = this.selectedFilters.filter(x => x.value !== undefined).reduce((acc, curr) => {
        if (curr.name.startsWith('custom_fields[')) {
          const field = curr.name.replace('custom_fields[', '').replace(']', '')
          const val = curr.value === '_null_' ? null : curr.value
          const clause = { field, op: curr.operator || 'equals', v: val }
          if (curr.negative) clause.not = true
          if (!Array.isArray(acc.custom_fields)) acc.custom_fields = []
          acc.custom_fields.push(JSON.stringify(clause))
        } else {
          const val = curr.value === '_null_' ? null : curr.value
          const clause = { op: curr.operator || 'equals', v: val }
          if (curr.negative) clause.not = true
          if (!Array.isArray(acc[curr.name])) acc[curr.name] = []
          acc[curr.name].push(JSON.stringify(clause))
        }
        return acc
      }, {})
      this.$emit('filters-changed', clauses)
    },
    getOperatorDisplayText(filter) {
      const operator = filter.operator || 'equals'
      const operatorDef = OperatorDisplayTexts[operator]
      
      // console.log('getOperatorDisplayText:', { filter, operator, operatorDef, negative: filter.negative })
      
      if (operatorDef) {
        return (filter.negative && filter.allowNegative !== false) ? operatorDef.negative : operatorDef.positive
      }

      // Fallback for non-TEXT filters or undefined operators
      return (filter.negative && filter.allowNegative !== false) ? `is NOT ${this.filterTypeOperator[filter.type]}` : this.filterTypeOperator[filter.type]
    },
    getFilterComponentName(filter) {
      const name = this.filterTypeComponent[filter.type].name
      if (typeof name == 'function') {
        return name(filter)
      } else {
        return name
      }
    },
    getFilterAttributes(filter) {
      const config = this.filterTypeComponent[filter.type]
      if (!config || !config.attributes) return {}

      if (typeof config.attributes === 'function') {
        return config.attributes(filter)
      }
      return config.attributes
    },
    getFiltersExpandedFromStorage() {
      const pref = this.$store.getters['UserPreferencesStore/getPreference']('selected_filters_visibility')
      return pref?.expanded !== undefined ? pref.expanded : true
    },
    saveFiltersExpandedToStorage(value) {
      this.$store.dispatch('UserPreferencesStore/set', {
        key: 'selected_filters_visibility',
        value: { expanded: value }
      })
    },
    toggleFiltersVisibility() {
      this.filtersExpanded = !this.filtersExpanded
      this.saveFiltersExpandedToStorage(this.filtersExpanded)
    },
    toggleShowAllFilters() {
      this.showAllFilters = !this.showAllFilters
      this.$store.dispatch('UserPreferencesStore/set', {
        key: 'custom_field_filters',
        value: { show_all: this.showAllFilters }
      })
    }
  },
  created() {
    this.reloadDynamicFilters = (() => {
      let timeout = null, promise = null, resolvePromise = null

      return () => {
        if (promise) {
          return promise;
        }

        promise = new Promise((resolve) => {
          resolvePromise = resolve;
        });

        clearTimeout(timeout);

        timeout = setTimeout(async () => {
          timeout = null;
          const result = await this._reloadDynamicFilters();
          resolvePromise(result);
          promise = null;
          resolvePromise = null;
        }, 50);

        return promise;
      };
    })();

    this.reloadDynamicFilters()
  },
  data() {
    return {
      FilterType,
      filterModel: null,
      selectedFilters: [],
      tempValue: {},
      tempNegative: {},
      tempOperator: {},
      tempBetweenFrom: {},
      tempBetweenTo: {},
      assignedColors: {},
      colors: [
        'var(--ld-status-success)',
        'var(--ld-status-info)',
        'var(--ld-brand-teal)',
        'color-mix(in srgb, var(--ld-status-warning) 80%, var(--ld-bg-secondary))',
        'color-mix(in srgb, var(--ld-status-danger) 70%, var(--ld-bg-secondary))',
        'var(--ld-brand-purple)',
        'var(--ld-status-warning)'
      ],
      filtersExpanded: this.getFiltersExpandedFromStorage(),
      filterTypeComponent: {
        [FilterType.TEXT]: { name: 'b-input', attributes: { placeholder: "Search Value" }},
        [FilterType.NUMBER]: {
          name: 'input-with-validation',
          attributes: filter => ({
            'b-name': 'input',
            'field-label': ' ',
            class: 'number-input',
            rules: filter.integerOnly ? 'integer' : 'decimal',
            placeholder: "Search Value"
          })
        },
        [FilterType.ENUM]: {
          name: filter => {
            return filter.isInline ? 'table-filter-radio-group' : 'b-select'
          }
        },
        [FilterType.DATERANGE]: { name: 'b-datepicker', attributes: { range: true, locale: 'en-US' }},
        [FilterType.DATETIMERANGE]: { name: 'b-datetimepicker', attributes: { range: true, locale: 'en-US' }},
        [FilterType.DATE]: { name: 'b-datepicker', attributes: { range: false, locale: 'en-US' }},
        [FilterType.AUTOCOMPLETE]: { name: 'AutocompleteTableFilter' }
      },
      filterTypeOperator: {
        [FilterType.TEXT]: 'contains',
        [FilterType.NUMBER]: 'is',
        [FilterType.ENUM]: 'is',
        [FilterType.DATERANGE]: 'between',
        [FilterType.DATETIMERANGE]: 'between',
        [FilterType.DATE]: 'is',
        [FilterType.AUTOCOMPLETE]: 'contains'
      },
      operatorLabels: {
        equals: 'equals',
        contains: 'contains',
        starts_with: 'starts with',
        ends_with: 'ends with',
        greater_than: 'greater than',
        less_than: 'less than',
        greater_than_or_equal: 'greater than or equal to',
        less_than_or_equal: 'less than or equal to',
        between: 'between'
      },
      showAllFilters: this.$store.getters['UserPreferencesStore/getPreference']('custom_field_filters')?.show_all || false,
      autocompleteData: {},
      dynamicEnumValues: {}
    }
  }
}
</script>

<style scoped lang="scss">
  .filters {
    //margin-bottom: 15px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px 0;
    > .dropdown {
      margin: 0 15px 0 0;
    }
  }
  .filter {
    display: flex;
    align-items: center;
    background: var(--ld-status-info);
    padding: 5px 8px;
    border-radius: 10px;
    cursor: pointer;
    height: 30px;
    p {
      margin: 0 !important;
      font-size: 14px;
    }
    span {
      font-size: 14px;
      line-height: 14px;
    }
    button {
      cursor: pointer;
      color: color-mix(in srgb, var(--ld-status-info) 65%, #000000);
      font-size: 16px;
      margin-left: 5px;
      padding: 0;
      border: none;
      background: none;
    }
  }
  .filter-content {
    display: flex;
    align-items: center;
    button {
      font-size: 13px;
      align-self: center;
    }
  }
  .negative-toggle {
    margin-bottom: 10px;
    line-height: 0;
    :deep(.checkbox) {
      font-size: 14px;
      font-weight: 400;
    }
  }
  .filter-buttons {
    display: flex;
    align-items: center;
    margin-right: 14px;
  }
  .filter-buttons.filter-buttons__clearable {
    border-right: 1px solid var(--ld-border-subtle);
  }
  .filter-buttons > .button {
    margin-left: 5px;
  }
  .filters-container {
    margin-bottom: 30px;
  }
  .selected-filters-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    > .dropdown {
      margin-left: 0 !important;
    }
  }
  @include mobile {
    .filters {
      flex-direction: column;
      align-items: flex-start;
    }
    .filter-buttons {
      width: 100%;
      border-right: none !important;
      margin-bottom: 10px;
      justify-content: center;
      border-top: 1px solid var(--ld-border-subtle);
      padding-top: 10px;
      border-top: 1px solid var(--ld-border-default);
    }
    .selected-filters-wrapper {
      width: 100%;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 5px;
      .filter-dropdown {
        margin: 0;
        display: block;
        :deep(.dropdown-trigger) {
          height: 100%;
        }
        .filter {
          height: 100%;
          padding: 8px;
        }
        .filter button {
          margin-left: auto;
        }
      }
    }
  }

  .filter-controls {
    display: flex;
    gap: 10px;
    align-items: center;
    
    .negative-toggle {
      flex-shrink: 0;
      margin-bottom: 0;
    }
    
    .operator-select {
      flex: 1;
      min-width: 120px;
    }
  }

  .between-inputs {
    display: flex;
    align-items: center;
    gap: 10px;

    :deep(.label), :deep(.has-text-danger)  {
      display: none !important;
    }
    :deep(.has-errors) {
      .control.has-icons-right .input {
        padding-right: 0;
      }
    }
    .between-separator {
      font-size: 14px;
    }
    :deep(input) {
      width: 75px;  
    }
  }

  .filter-control-input {
    margin: 0 10px;
    min-width: 130px;
    width: 100%;
  }
  :deep(.number-input) {
    label, p.help {
      display: none;
    }
  }
  :deep(.validation-observer > div) {
    display: flex;
    .field-wrapper {
      margin-bottom: 0;
    }
  }
  .show-all-filters {
    display: flex;
    align-items: center;
    gap: 5px;
    color: $primary;
    font-size: 13px;
    cursor: pointer;
    padding: 4px 0;
    &:hover {
      text-decoration: underline;
    }
  }

</style>
